import type { CycleRecord, DailyRecord, DataSource, HealthMetric } from '../../types/health';

type MetricDisplay = {
  label: string;
  value: string;
  metadata: string;
  source?: DataSource;
  estimated?: boolean;
};

type TodayInput = {
  metrics: HealthMetric[];
  cycles: CycleRecord[];
  record?: DailyRecord;
  now?: Date;
  timeZone: string;
};

const missing = '—';
const finite = (value: number | undefined): value is number => typeof value === 'number' && Number.isFinite(value);

export function createTodaySummary({ metrics, cycles, record, now = new Date(), timeZone }: TodayInput) {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
  const currentRecord = record?.date === today ? record : undefined;
  const title = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long', month: 'long', day: 'numeric' }).format(now);

  function display(label: string, type: HealthMetric['type'], format: (value: number) => string): MetricDisplay {
    const metric = metrics.filter((item) => item.type === type && finite(item.value))
      .sort((a, b) => b.startDate.localeCompare(a.startDate))[0];
    return metric
      ? { label, value: format(metric.value), metadata: `Recorded ${metric.startDate.slice(0, 10)}`, source: metric.source }
      : { label, value: missing, metadata: 'No imported data yet' };
  }

  const cycle = cycles.find((item) => !item.isEstimated && item.date === today && (finite(item.cycleDay) || (item.source === 'apple_health' && Boolean(item.sourceId))));
  const nextCycle = cycles.filter((item) => item.isEstimated && item.date >= today && /^\d{4}-\d{2}-\d{2}$/.test(item.date))
    .sort((a, b) => a.date.localeCompare(b.date))[0];
  const displays: MetricDisplay[] = [
    display('Sleep', 'sleep_duration', (value) => `${Math.floor(Math.round(value * 60) / 60)}h ${Math.round(value * 60) % 60}m`),
    display('Weight', 'weight', (value) => `${value.toFixed(1)} kg`),
    cycle
      ? { label: 'Cycle', value: finite(cycle.cycleDay) ? `Day ${cycle.cycleDay}` : cycle.flow ?? 'Unspecified', metadata: `Recorded ${cycle.date}`, source: cycle.source }
      : { label: 'Cycle', value: missing, metadata: 'No cycle record for today' },
    display('Steps', 'steps', (value) => value.toLocaleString('en-US')),
    display('Resting HR', 'resting_heart_rate', (value) => `${value} bpm`),
    display('HRV', 'hrv', (value) => `${value} ms`),
    nextCycle
      ? { label: 'Next period', value: nextCycle.date, metadata: 'Estimated from a recorded cycle', source: nextCycle.source, estimated: true }
      : { label: 'Next period', value: missing, metadata: 'No estimate available' },
    display('Sleep source', 'sleep_duration', (value) => `${value.toFixed(2)} h`),
  ];

  return {
    title,
    metrics: displays,
    readinessScore: finite(currentRecord?.readinessScore) ? String(currentRecord.readinessScore) : missing,
    readinessStatus: finite(currentRecord?.readinessScore) && currentRecord?.readinessStatus
      ? currentRecord.readinessStatus.toUpperCase() : 'NO DATA',
    readinessNote: currentRecord?.notes ?? 'No readiness assessment recorded for today.',
    energy: finite(currentRecord?.energy) ? `${currentRecord.energy} / 5` : missing,
    soreness: finite(currentRecord?.soreness) ? `${currentRecord.soreness} / 5` : missing,
  };
}
